import { Injectable } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { GridOptions } from 'ag-grid-community';
import { jsPDF } from 'jspdf';
import { autoTable, UserOptions } from 'jspdf-autotable';
import { isEmpty, isObject, isUndefined, sum } from 'lodash';

import { EmployeeScorecardHelperService } from '@ag-grid-reports/employee-scorecard-report/services/employee-scorecard-helper.service';
import { ColumnFieldClass } from '@services/preferences/preferences.enum';
import { PreferencesService } from '@services/preferences/preferences.service';
import { AgGridReportsService } from '@services/reports/ag-grid-reports.service';
import { IPdfReportOptions } from '@services/reports/models/pdf-report-options.model';

@Injectable({
    providedIn: 'root',
})
export class AgGridPdfExportService {
    private pdfFooterColumns: any[] = [];

    constructor(
        private agGridReportsService: AgGridReportsService,
        private preferencesService: PreferencesService,
        private employeeScorecardHelperService: EmployeeScorecardHelperService,
    ) {}

    public generatePDF(
        gridOptions: GridOptions,
        reportOptions: IPdfReportOptions,
        translationService: TranslateService,
    ): void {
        let gridWidth = 0;
        let groupedColumnsForExporting = 0;
        const headerColumns = [];
        const body = [];
        const footerColumns = [];

        this.pdfFooterColumns = [];

        gridOptions.columnApi.getAllDisplayedColumns().forEach((tempItem, index) => {
            let item;
            let isGrouped = false;
            if (tempItem.getUserProvidedColDef()) {
                item = tempItem;
            } else {
                const groupColumnId = tempItem.getColId().slice(19, tempItem.getColId().length);
                item = gridOptions.columnApi.getAllColumns().filter((data) => data.getColId() === groupColumnId)[0];
                isGrouped = true;
                groupedColumnsForExporting++;
            }

            headerColumns.push(item.userProvidedColDef.headerName);
            gridWidth += item.minWidth;
            gridOptions.api.forEachNodeAfterFilterAndSort((rowNode, indexNode) => {
                if (index === 0) {
                    body.push([]);
                }

                if (isObject(rowNode.data) && item.colId in rowNode.data) {
                    this.fillArrayWithValues(item, index, rowNode, indexNode);

                    switch (item.colId) {
                        case 'customer__first_name':
                            if (rowNode.data[item.colId] !== '(None)') {
                                body[indexNode].push(
                                    `${rowNode.data[item.colId]} ${rowNode.data['customer__last_name']}`,
                                );
                            } else {
                                body[indexNode].push(
                                    this.formatterForFieldsInPdf(translationService, item, index, rowNode),
                                );
                            }
                            break;
                        case 'calculated_avg_transaction':
                            body[indexNode].push(
                                this.currencyFormatter(
                                    this.employeeScorecardHelperService.calculateAVG(
                                        rowNode.data['net_sales'],
                                        rowNode.data['total_orders'],
                                    ),
                                ),
                            );
                            break;
                        case 'calculated_sales_per_labor_hour':
                            body[indexNode].push(
                                this.currencyFormatter(
                                    this.employeeScorecardHelperService.calculateSalesPerLaborHour(
                                        rowNode.data['net_sales'],
                                        rowNode.data['labor_hours'],
                                    ),
                                ),
                            );
                            break;
                        case 'calculated_guests_per_labor_hour':
                            body[indexNode].push(
                                this.employeeScorecardHelperService.calculateSalesPerLaborHour(
                                    rowNode.data['guests'],
                                    rowNode.data['labor_hours'],
                                ),
                            );
                            break;
                        default:
                            if (!isGrouped) {
                                body[indexNode].push(
                                    this.formatterForFieldsInPdf(translationService, item, index, rowNode),
                                );
                            } else {
                                body[indexNode].push('');
                            }
                    }
                } else {
                    if (isGrouped && rowNode.groupData) {
                        body[indexNode].push(
                            rowNode.groupData[tempItem.getColId()]
                                ? this.formatterForGroupFieldsInPdf(translationService, tempItem, rowNode.groupData)
                                : rowNode.groupData[tempItem.getColId()],
                        );
                    } else if (item.colId in rowNode.aggData) {
                        body[indexNode].push(
                            this.formatterForGroupFieldsInPdf(translationService, item, rowNode.aggData, true),
                        );
                    } else {
                        body[indexNode].push('');
                    }
                }
            });

            if (index === 0) {
                footerColumns.push('Total');
            } else {
                footerColumns.push(this.formatterForFieldsInPdf(translationService, item, index, {}, true));
            }
        });

        if (body.length === 0) {
            footerColumns.length = 0;
        }

        const doc = this.initPdfDoc(
            {
                gridWidth,
                groupedColumnsForExporting,
                headerColumns,
                footerColumns,
                body,
            },
            reportOptions,
            translationService,
        );

        doc.save(reportOptions.reportTitle + '.pdf');
    }

    private initPdfDoc(options, reportOptions: IPdfReportOptions, translationService: TranslateService) {
        const { gridWidth, groupedColumnsForExporting, headerColumns, footerColumns, body } = options;
        const minLandscapeWidth = 900;
        const totalPagesExp = '{total_pages_count_string}';

        let doc: jsPDF;
        if (gridWidth >= minLandscapeWidth) {
            doc = new jsPDF('landscape');
        } else {
            doc = new jsPDF();
        }

        doc.setFontSize(16);
        doc.text(reportOptions.reportTitle, 14, 18);
        doc.setFontSize(9);
        doc.setLineHeightFactor(1.2);
        doc.setTextColor(153, 153, 153);
        let headerText = `Business Date: ${
            this.agGridReportsService.areEqualDates([reportOptions.dateFrom, reportOptions.dateTo])
                ? this.dateFormatter(reportOptions.dateFrom, translationService)
                : this.dateFormatter(reportOptions.dateFrom, translationService) +
                  ' - ' +
                  this.dateFormatter(reportOptions.dateTo, translationService)
        }`;
        if (!isUndefined(reportOptions.count.value)) {
            headerText =
                headerText +
                `
${translationService.instant('COUNT_TOTAL')}: ${this.numberFormatter(reportOptions.count.value)}`;
        }

        doc.text(headerText, 14, 22);
        autoTable(doc, {
            head: [headerColumns],
            body,
            foot: [footerColumns],
            ...this.getDefaultParamsForColumns(),
            didParseCell: (data) => {
                if (groupedColumnsForExporting > 0 && data.row.section === 'body') {
                    if (!isEmpty(data.row.raw[0]) && isEmpty(data.row.raw[1])) {
                        data.cell.styles.cellWidth = 'auto';
                    } else if (
                        groupedColumnsForExporting === 2 &&
                        isEmpty(data.row.raw[0]) &&
                        !isEmpty(data.row.raw[1])
                    ) {
                        data.cell.styles.cellWidth = 'auto';
                    }
                }
                if (data.row.section === 'body') {
                    const textValue = Array.isArray(data.cell.text) ? data.cell.text[0] : data.cell.text;
                    if (typeof textValue === 'string' && parseFloat(textValue) < 0) {
                        data.cell.styles.cellWidth = 22;
                    }
                }
            },
            willDrawCell: (data) => {
                if (data.row.section === 'body') {
                    if (typeof data.cell.raw === 'string' && data.cell.raw?.indexOf('red') > -1) {
                        doc.setTextColor(231, 76, 60);
                        data.cell.text[0] = data.cell.raw.slice(
                            data.cell.raw.indexOf('>') + 1,
                            data.cell.raw.indexOf('</'),
                        );
                    }
                    if (typeof data.cell.raw === 'number' && data.cell.raw < 0) {
                        doc.setTextColor(231, 76, 60);
                        data.cell.text = [this.currencyFormatter(data.cell.raw, 'detail')];
                    }
                }

                if (data.row.section === 'foot') {
                    if (typeof data.cell.raw === 'string' && data.cell.raw?.indexOf('red') > -1) {
                        doc.setTextColor(231, 76, 60);
                        data.cell.text[0] = data.cell.raw.slice(
                            data.cell.raw.indexOf('>') + 1,
                            data.cell.raw.indexOf('</'),
                        );
                    }
                }

                if (groupedColumnsForExporting > 0) {
                    if (!isEmpty(data.row.raw[0])) {
                        doc.setFont('Helvetica', 'Bold');
                        if (data.cell.text[0].indexOf('...') !== -1 && data.row.section === 'body') {
                            data.cell.text[0] = data.cell.text[0].slice(0, -4) + '...';
                        }
                    } else if (
                        groupedColumnsForExporting === 2 &&
                        isEmpty(data.row.raw[0]) &&
                        !isEmpty(data.row.raw[1])
                    ) {
                        doc.setFont('Helvetica', 'Bold');
                        if (data.cell.text[0].indexOf('...') !== -1 && data.row.section === 'body') {
                            data.cell.text[0] = data.cell.text[0].slice(0, -4) + '...';
                        }
                    }
                }
            },
            didDrawPage: (data) => {
                let str = 'Page ' + doc.getNumberOfPages();
                if (typeof doc.putTotalPages === 'function') {
                    str = str + ' of ' + totalPagesExp;
                }
                doc.setFontSize(10);

                const pageSize = doc.internal.pageSize;
                const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
                doc.text(str, data.settings.margin.left, pageHeight - 10);
            },
        });

        if (typeof doc.putTotalPages === 'function') {
            doc.putTotalPages(totalPagesExp);
        }

        return doc;
    }

    private getDefaultParamsForColumns(): UserOptions {
        return {
            showFoot: 'lastPage',
            startY: 30,
            margin: 0,
            // Default for all columns
            styles: {
                overflow: 'ellipsize',
                cellWidth: 'wrap',
                fontSize: 6,
            },
            headStyles: {
                fillColor: '#ffffff',
                textColor: '#333333',
                fontSize: 7,
                cellWidth: 'auto',
            },
            footStyles: {
                fillColor: '#e1e1e1',
                textColor: '#000',
                fontSize: 8,
                cellWidth: 'auto',
            },
            // Override the default above for the text column
            columnStyles: {
                site: { cellWidth: 'auto' },
                employee: { cellWidth: 'auto' },
                customer__first_name: { cellWidth: 'auto' },
            },
        };
    }

    private fillArrayWithValues(item, index: number, rowNode, _indexNode: number): void {
        if (!this.pdfFooterColumns[index]) {
            this.pdfFooterColumns[index] = [];
        }

        if (
            item.userProvidedColDef.cellClass &&
            (item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_PRICE) >= 0 ||
                item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_COUNT) >= 0 ||
                item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_PERCENTS) >= 0)
        ) {
            this.pdfFooterColumns[index].push(rowNode.data[item.colId]);
        } else {
            this.pdfFooterColumns[index].push('');
        }
    }

    private formatterForFieldsInPdf(
        translationService: TranslateService,
        item,
        index,
        rowNode: any = {},
        footer = false,
    ): string | number {
        if (
            item.userProvidedColDef &&
            item.userProvidedColDef.cellClass &&
            item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_PRICE) >= 0
        ) {
            if (!footer) {
                return Number.parseFloat(rowNode.data[item.colId]) >= 0
                    ? this.currencyFormatter(rowNode.data[item.colId])
                    : rowNode.data[item.colId];
            } else {
                if (!item.aggFunc) {
                    return '';
                }
                switch (item.aggFunc) {
                    case 'customAvgSum':
                    case 'customAvgSumAggFunc':
                        return this.currencyFormatter(this.getTotalAvgValueById(item.colId));
                    case 'customXVISumAggFunc':
                        return this.xviCurrencyFormatterForAgGrid(
                            this.agGridReportsService.getTotalSumValues()[item.colId],
                        );
                    case 'customXVISumAggForSpecificFunc':
                        return this.agGridReportsService.getTotalSumValues()[item.colId];
                    default:
                        return this.currencyFormatter(this.agGridReportsService.getTotalSumValues()[item.colId]);
                }
            }
        } else if (
            item.userProvidedColDef &&
            item.userProvidedColDef.cellClass &&
            item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_COUNT) >= 0
        ) {
            return !footer
                ? this.numberFormatter(rowNode.data[item.colId])
                : this.numberFormatter(sum(this.pdfFooterColumns[index]?.map((value) => +value)));
        } else if (
            item.userProvidedColDef &&
            item.userProvidedColDef.cellClass &&
            item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_PERCENTS) >= 0
        ) {
            let value = this.pdfFooterColumns[index].reduce((acc, current) => acc + +current, 0);
            if (value !== 0) {
                value = (value * 100) / this.pdfFooterColumns[index].length;
            }
            return !footer ? this.percentFormatter(rowNode.data[item.colId] * 100) : this.percentFormatter(value);
        } else {
            if (['customAvgSumAggFunc', 'customXVISumAggFunc', 'customXVISumAggForGrouping'].includes(item.aggFunc)) {
                if (!footer) {
                    return item.colDef.cellRenderer
                        ? item.colDef.cellRenderer(rowNode.data[item.colId])
                        : rowNode.data[item.colId];
                } else {
                    return item.colDef.cellRenderer
                        ? item.colDef.cellRenderer(this.getTotalAvgValueById(item.colId))
                        : this.getTotalAvgValueById(item.colId);
                }
            }
            if (footer) {
                return '';
            }

            if (item.colId === 'business_date') {
                return this.preferencesService.dateFormatter(rowNode.data[item.colId], translationService);
            } else if (
                this.agGridReportsService.isDateValueInField(item, rowNode.data[item.colId]) &&
                item.colId.indexOf('ag-Grid-AutoColumn') === -1
            ) {
                return this.preferencesService.dateAndTimeFormatter(rowNode.data[item.colId], translationService);
            } else {
                return item.colDef.cellRenderer
                    ? item.colDef.cellRenderer(rowNode.data[item.colId])
                    : rowNode.data[item.colId];
            }
        }
    }

    private formatterForGroupFieldsInPdf(
        translationService: TranslateService,
        item,
        data: any = {},
        aggregatedData = false,
    ): string | number {
        if (aggregatedData && item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_PRICE) >= 0) {
            return data[item.colId] >= 0 ? this.currencyFormatter(data[item.colId]) : data[item.colId];
        } else if (aggregatedData && item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_COUNT) >= 0) {
            return this.numberFormatter(data[item.colId]);
        } else if (
            aggregatedData &&
            item.userProvidedColDef.cellClass[0].indexOf(ColumnFieldClass.CUSTOM_PERCENTS) >= 0
        ) {
            return this.percentFormatter(data[item.colId] * 100);
        } else {
            if (item.colId.indexOf('business_date') > -1) {
                return this.preferencesService.dateFormatter(data[item.colId], translationService);
            } else if (this.agGridReportsService.isDateValueInField(item, data[item.colId])) {
                return this.preferencesService.dateAndTimeFormatter(data[item.colId], translationService);
            } else {
                return data[item.colId];
            }
        }
    }

    private dateFormatter(value, translationService: TranslateService): string {
        if (value === undefined || value === null) {
            return '';
        } else if (value === '(None)') {
            return translationService.instant('NONE');
        } else {
            return this.preferencesService.dateFormatter(value, translationService);
        }
    }

    private numberFormatter(val): string | number {
        return this.preferencesService.numberFormatterForAgGrid(val);
    }

    private currencyFormatter(params, type = 'grid') {
        return this.preferencesService.currencyFormatterForAgGrid(params, type);
    }
    private xviCurrencyFormatterForAgGrid(params) {
        return this.preferencesService.xviCurrencyFormatterForAgGrid(params);
    }

    private percentFormatter(params) {
        return this.preferencesService.formatToPercentage(params);
    }

    private getTotalAvgValueById(id: string): number {
        switch (id) {
            case 'orders_avg':
                return this.agGridReportsService.getTotalAverageValues('orders_avg');
            case 'guest_avg':
                return this.agGridReportsService.getTotalAverageValues('guest_avg');
            case 'calculated_avg_transaction':
            case 'calculated_sales_per_labor_hour':
            case 'calculated_guests_per_labor_hour':
                return this.agGridReportsService.getTotalAverageValues(id);
            default:
                return this.agGridReportsService.getCustomAvgValue(id);
        }
    }
}
